import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
	FiChevronLeft,
	FiChevronRight,
	FiChevronsLeft,
	FiChevronsRight,
	FiExternalLink,
	FiPlus,
	FiRefreshCw,
	FiSearch
} from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { SkeletonCards, SkeletonList } from "../../Components/Skeleton";
import ConnectionsModalButton from "../../Components/ConnectionsModalButton";
import { client } from "../../Clients";
import type { APAADMINBaseModelsContentListItem, APAADMINBaseModelsContentTypeFilterItem, ContentDocument, ContentTypeConfig } from "../../API/Models";
import type { IApplicationState } from "../../Redux/Reducers";
import { hasAnyFieldValue, validateContentItemValues } from "../../Components/ContentItemForm";
import { showSuccessModal } from "../../Helper/showSuccessModal";
import TaxonomyMultiSelect from "../../Components/TaxonomyMultiSelect";
import CategoryPathSelect from "../../Components/CategoryPathSelect";

import {
	DEFAULT_LANGUAGE,
	DEFAULT_DOMAIN,
	PAGE_SIZE_OPTIONS,
	SINGLETON_KEYS,
	type ViewMode,
	type ContentTypeFilter,
	getQueryType,
	normalizeContentTypeKey,
	resolveDefaultFieldKey,
	buildTodayValue,
	buildDefaultComponentsFromMarkdown,
	resolveContentTypeConfig,
	isHiddenListField,
	isHiddenBrowseType,
	compareBrowseOrder,
	categorizeContentType,
	CONTENT_CATEGORIES
} from "./utils";
import ContentListView from "./ContentListView";
import ContentGridView from "./ContentGridView";
import { ContentTypePickerModal, CreateContentFormModal } from "./CreateContentModal";

/** Paginated dynamic content list with type filtering, search and new item creation. */
const Content: React.FC = () => {
	const history = useHistory();
	const location = useLocation();
	const { contentTypeId } = useParams<{ contentTypeId?: string }>();
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);
	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const categories = useSelector((state: IApplicationState) => state.categories.items);
	const [items, setItems] = useState<APAADMINBaseModelsContentListItem[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
	const [loadingList, setLoadingList] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [viewMode, setViewMode] = useState<ViewMode>("list");
	// Sort column + direction. Default = newest first ("latest first"), applied server-side so it holds
	// across pagination, not just the loaded page.
	const [sort, setSort] = useState<{ field: string; desc: boolean }>({ field: "updated", desc: true });
	// Monotonic request id: the type/sort default resolves in two steps (updated → date-field once the
	// config loads), so several list calls overlap. Only the latest one's response may touch state —
	// otherwise a slower earlier (e.g. "updated") response lands last and clobbers the date-sorted rows.
	const loadSeqRef = useRef(0);
	const [contentTypeFilter, setContentTypeFilter] = useState<ContentTypeFilter>(() => getQueryType(location.search));
	const [availableTypes, setAvailableTypes] = useState<APAADMINBaseModelsContentTypeFilterItem[]>([]);
	const [taxonomyFilterIds, setTaxonomyFilterIds] = useState<string[]>([]);
	const [categoryFilterPathIds, setCategoryFilterPathIds] = useState<string[]>([]);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [createType, setCreateType] = useState<ContentTypeConfig | null>(null);
	const [createValues, setCreateValues] = useState<Record<string, any>>({});
	const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
	const [createFormError, setCreateFormError] = useState<string | null>(null);
	const [creating, setCreating] = useState(false);

	usePageHeader({ title: "Content" });

	const decodedContentTypeId = useMemo(() => {
		if (!contentTypeId) return null;
		try {
			return decodeURIComponent(contentTypeId);
		} catch {
			return contentTypeId;
		}
	}, [contentTypeId]);
	const queryType = useMemo(() => {
		return getQueryType(location.search);
	}, [location.search]);
	const lockedTypeKey = useMemo(() => {
		if (!decodedContentTypeId) return null;
		const normalizedId = decodedContentTypeId.trim().toLowerCase();
		const byId = (contentTypeConfigs ?? []).find((item) => item.id?.toLowerCase() === normalizedId);
		if (byId?.id) return byId.id;
		const byKey = (contentTypeConfigs ?? []).find(
			(item) => normalizeContentTypeKey(item.title ?? "") === normalizedId
		);
		if (byKey?.id) return byKey.id;
		return decodedContentTypeId.trim();
	}, [contentTypeConfigs, decodedContentTypeId]);
	const lockedConfig = useMemo(
		() => resolveContentTypeConfig(contentTypeConfigs ?? [], decodedContentTypeId),
		[contentTypeConfigs, decodedContentTypeId]
	);
	const isTypeLocked = Boolean(lockedTypeKey);
	const effectiveContentType = lockedTypeKey ?? contentTypeFilter;
	const showPages = effectiveContentType === "all" || effectiveContentType === "page";
	const slugColumnLabel =
		effectiveContentType === "page" ? "Slug" : effectiveContentType === "all" ? "Slug / ID" : "ID";
	const listFields = useMemo(() => {
		if (effectiveContentType === "all") return [];
		const config = lockedConfig ?? resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		// Drop internal/migration bookkeeping columns (e.g. the imported Drupal node id).
		const visible = (config?.fields ?? []).filter((field) => field.showInList && !isHiddenListField(field));
		// Product records expose many operational fields. The catalogue list keeps the six values
		// needed to identify, price and fulfil a part; every other field remains editable in the form.
		if (normalizeContentTypeKey(config?.id ?? effectiveContentType) === "product") {
			const productColumns = new Set(["family", "reference", "price", "stockstatus", "stockquantity", "verificationstatus"]);
			return visible.filter((field) => productColumns.has(normalizeContentTypeKey(field.key ?? field.title ?? "")));
		}
		return visible;
	}, [contentTypeConfigs, effectiveContentType, lockedConfig]);
	const showPathColumn = useMemo(() => {
		if (effectiveContentType === "page") return false;
		if (items.some((item) => Boolean(item.path))) return true;
		if (effectiveContentType === "all") {
			return (contentTypeConfigs ?? []).some((config) => config.supportsDynamicPath);
		}
		const config = resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		return Boolean(config?.supportsDynamicPath);
	}, [contentTypeConfigs, effectiveContentType, items]);
	const searchPlaceholder = showPages
		? "Search pages by slug, title, or description"
		: "Search content by title";
	const supportsTaxonomyFilter = useMemo(() => {
		if (effectiveContentType === "page") return true;
		if (effectiveContentType === "all") return true;
		if (lockedConfig?.supportsTaxonomy) return true;
		const config = resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		return Boolean(config?.supportsTaxonomy);
	}, [contentTypeConfigs, effectiveContentType, lockedConfig]);
	const supportsCategoryFilter = useMemo(() => {
		if ((categories ?? []).length === 0) return false;
		if (effectiveContentType === "all") return true;
		if (effectiveContentType === "page") return false;
		if (lockedConfig?.supportsCategory) return true;
		const config = resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		return Boolean(config?.supportsCategory);
	}, [categories, contentTypeConfigs, effectiveContentType, lockedConfig]);
	const updateTypeParam = useCallback(
		(next: string) => {
			const params = new URLSearchParams(location.search);
			if (next === "all") {
				params.delete("type");
			} else {
				params.set("type", next);
			}
			const nextSearch = params.toString();
			const currentSearch = new URLSearchParams(location.search).toString();
			if (nextSearch !== currentSearch) {
				history.replace({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" });
			}
		},
		[history, location.pathname, location.search]
	);

	const loadList = useCallback(
		async (pageNum: number, size: number) => {
			const seq = ++loadSeqRef.current;
			setLoadingList(true);
			setError(null);
			try {
				const taxonomyFilter = taxonomyFilterIds.length ? taxonomyFilterIds.join(",") : undefined;
				const categoryId = categoryFilterPathIds.length
					? categoryFilterPathIds[categoryFilterPathIds.length - 1]
					: undefined;
				const res = await client.content.apiContentListGet(
					pageNum,
					size,
					search.trim() || undefined,
					undefined,
					effectiveContentType === "all" ? undefined : effectiveContentType,
					taxonomyFilter,
					categoryId,
					sort.field,
					sort.desc
				);
				if (seq !== loadSeqRef.current) return; // superseded by a newer request — drop this stale response
				const data = res.data;
				setItems(data?.items ?? []);
				setTotalCount(data?.totalCount ?? 0);
				setPage(data?.page ?? 1);
				setAvailableTypes(data?.contentTypes ?? []);
			} catch (err) {
				if (seq !== loadSeqRef.current) return;
				console.error(err);
				setError("Failed to load content list.");
				setItems([]);
				setTotalCount(0);
			} finally {
				if (seq === loadSeqRef.current) setLoadingList(false);
			}
		},
		[effectiveContentType, search, taxonomyFilterIds, categoryFilterPathIds, sort]
	);

	useEffect(() => {
		if (lockedTypeKey) {
			if (contentTypeFilter !== lockedTypeKey) {
				setContentTypeFilter(lockedTypeKey);
				setPage(1);
			}
			return;
		}
		if (queryType !== contentTypeFilter) {
			setContentTypeFilter(queryType);
			setPage(1);
		}
	}, [queryType, contentTypeFilter, lockedTypeKey]);

	/** The selected type's primary date field — a `type: "date"` field, else one named like a date.
	 * Used to default the table to "newest first BY DATE", which is more meaningful than the admin's
	 * last-modified timestamp (identical across migration-imported rows). Null for "all"/pages/dateless. */
	const dateFieldKey = useMemo(() => {
		if (effectiveContentType === "all" || showPages) return null;
		const config = lockedConfig ?? resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		const fields = config?.fields ?? [];
		const byType = fields.find((field) => (field.type ?? "").trim().toLowerCase() === "date");
		const byName = fields.find((field) => {
			const norm = normalizeContentTypeKey(field.key ?? field.title ?? "");
			return norm.split("-").includes("date") || norm.includes("publication");
		});
		return (byType ?? byName)?.key ?? null;
	}, [effectiveContentType, showPages, lockedConfig, contentTypeConfigs]);

	useEffect(() => {
		setTaxonomyFilterIds([]);
		setCategoryFilterPathIds([]);
		// Default a type's table to its own date field, newest first; fall back to last-modified for the
		// "all" view and types that have no date field.
		setSort(dateFieldKey ? { field: `field:${dateFieldKey}`, desc: true } : { field: "updated", desc: true });
	}, [effectiveContentType, dateFieldKey]);

	/** Toggle direction when re-clicking the active column, else select it. Dates start newest-first,
	 * text columns start A→Z. Sorting is server-side so it spans the whole result set, not just the page. */
	const handleSort = useCallback((field: string) => {
		setSort((prev) => (prev.field === field ? { field, desc: !prev.desc } : { field, desc: field === "updated" }));
		setPage(1);
	}, []);

	/** The selected type only gets a Title column when it actually has a title field (pages + the "all"
	 * view always do). Avoids a column full of blanks / "Untitled" for pure data types. */
	const hasTitleColumn = useMemo(() => {
		if (effectiveContentType === "all" || showPages) return true;
		const config = lockedConfig ?? resolveContentTypeConfig(contentTypeConfigs ?? [], effectiveContentType);
		if (normalizeContentTypeKey(config?.id ?? effectiveContentType) === "product") return true;
		return (config?.fields ?? []).some((field) => normalizeContentTypeKey(field.key ?? field.title ?? "") === "title");
	}, [effectiveContentType, showPages, lockedConfig, contentTypeConfigs]);

	useEffect(() => {
		loadList(page, pageSize);
	}, [page, pageSize, loadList]);

	const onFilterOrSearchChange = () => {
		setPage(1);
		loadList(1, pageSize);
	};
	const onTaxonomyFilterChange = (next: string[]) => {
		setTaxonomyFilterIds(next);
		setPage(1);
	};
	const onCategoryFilterChange = (next: string[]) => {
		setCategoryFilterPathIds(next);
		setPage(1);
	};
	const resetCreateState = () => {
		setCreateType(null);
		setCreateValues({});
		setCreateErrors({});
		setCreateFormError(null);
	};
	const buildDefaultValues = useCallback((fields: ContentTypeConfig["fields"] = []) => {
		const defaults: Record<string, any> = {};
		(fields ?? []).forEach((field, index) => {
			const key = resolveDefaultFieldKey(field, index);
			if (!key) return;
			const fieldType = (field?.type ?? "").trim().toLowerCase();
			if (fieldType === "date" && field.defaultToday) {
				defaults[key] = buildTodayValue();
				return;
			}
			if (fieldType === "components" && field.defaultMarkdown) {
				const components = buildDefaultComponentsFromMarkdown(field.defaultMarkdown);
				if (components.length > 0) {
					defaults[key] = components;
				}
			}
		});
		return defaults;
	}, []);
	const handleOpenCreate = () => {
		resetCreateState();
		if (isTypeLocked) {
			if (!lockedConfig) {
				setCreateFormError("This content type is not available.");
				return;
			}
			setCreateType(lockedConfig);
			setCreateValues(buildDefaultValues(lockedConfig.fields ?? []));
			setShowCreateForm(true);
			return;
		}
		setShowCreateModal(true);
	};
	const handleSelectCreateType = (type: ContentTypeConfig) => {
		resetCreateState();
		setCreateType(type);
		setCreateValues(buildDefaultValues(type.fields ?? []));
		setShowCreateModal(false);
		setShowCreateForm(true);
	};
	const handleCreate = async () => {
		if (!createType?.title) return;
		const schemaKey = createType.id ?? normalizeContentTypeKey(createType.title);
		const hasCategoryValue =
			Array.isArray(createValues?.categoryPathIds) && createValues.categoryPathIds.length > 0;
		if (!hasAnyFieldValue(createType.fields ?? [], createValues) && !hasCategoryValue) {
			setCreateFormError("Fill at least one field before creating.");
			return;
		}
		const validation = validateContentItemValues(createType.fields ?? [], createValues);
		setCreateErrors(validation.errors);
		if (validation.hasErrors) {
			setCreateFormError("Please fix the highlighted fields.");
			return;
		}
		setCreateFormError(null);
		setCreating(true);
		try {
			const payloadDoc: ContentDocument = {
				contentType: schemaKey,
				language: DEFAULT_LANGUAGE,
				isDisabled: false,
				isDeleted: false,
				data: createValues
			};
			const response = await client.content.apiContentSchemaKeyLanguagePut(schemaKey, DEFAULT_LANGUAGE, undefined, payloadDoc);
			const createdId = response.data?.current?.id ?? null;
			setShowCreateForm(false);
			setShowCreateModal(false);
			resetCreateState();
			setPage(1);
			await loadList(1, pageSize);
			const routeType = createType.id ?? schemaKey;
			showSuccessModal({
				title: "Content created",
				message: `${createType.title} has been created.`,
				actionLabel: createdId ? "View content" : undefined,
				onAction: createdId
					? () => history.push(`/content/${encodeURIComponent(routeType)}/${encodeURIComponent(createdId)}`)
					: undefined
			});
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? "Create failed.";
			setCreateFormError(String(apiMessage));
		} finally {
			setCreating(false);
		}
	};

	const goToPage = (p: APAADMINBaseModelsContentListItem) => {
		const langCode = p.language || DEFAULT_LANGUAGE;
		const contentTypeKey = (p.contentType ?? "").toLowerCase();
		if (contentTypeKey !== "page") {
			const route = contentTypeKey === "layout"
				? "/layout"
				: contentTypeKey === "languages"
					? "/languages"
					: contentTypeKey === "contact-info"
						? "/contact-info"
						: null;
			if (route) history.push(route);
			if (!route) {
				const config = resolveContentTypeConfig(contentTypeConfigs ?? [], p.contentType ?? "");
				const routeType = config?.id ?? p.contentType;
				if (routeType && p.id) {
					history.push(`/content/${encodeURIComponent(routeType)}/${encodeURIComponent(p.id)}`);
				}
			}
			return;
		}
		if (p.id) {
			history.push(`/page/${encodeURIComponent(p.id)}`);
		} else {
			history.push(`/page/${encodeURIComponent(langCode)}/${encodeURIComponent(p.slug || "")}`);
		}
	};

	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
	const canPrev = page > 1;
	const canNext = page < totalPages;
	const goToPageNum = (next: number) => {
		const clamped = Math.min(Math.max(1, next), totalPages);
		if (clamped !== page) loadList(clamped, pageSize);
	};

	const createableTypes = useMemo(
		() =>
			(contentTypeConfigs ?? [])
				.filter((item) => {
					const key = normalizeContentTypeKey(item.title ?? "");
					return Boolean(item.title) && !SINGLETON_KEYS.has(key);
				})
				.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? "", undefined, { sensitivity: "base" })),
		[contentTypeConfigs]
	);
	const configFilters = useMemo(
		() =>
			(contentTypeConfigs ?? [])
				.filter((item) => item?.title)
				.map((item) => ({
					key: item.id ?? normalizeContentTypeKey(item.title ?? ""),
					title: item.title ?? "",
					isMain: item.isMain ?? false
				})),
		[contentTypeConfigs]
	);
	const mergedTypes = useMemo(() => {
		const map = new Map<string, APAADMINBaseModelsContentTypeFilterItem>();
		availableTypes.forEach((entry) => {
			if (!entry.key) return;
			map.set(entry.key, entry);
		});
		configFilters.forEach((entry) => {
			if (!entry.key) return;
			const existing = map.get(entry.key);
			if (existing) {
				map.set(entry.key, {
					...existing,
					...entry,
					title: existing.title ?? entry.title,
					isMain: existing.isMain ?? entry.isMain
				});
			} else {
				map.set(entry.key, entry);
			}
		});
		return Array.from(map.values())
			// Hide non-editorial / singleton types (layout, languages, contact-info, site-settings,
			// sitemap) from the browser — they're edited from their own sidebar pages or aren't content.
			.filter((entry) => !isHiddenBrowseType(entry.key, entry.title))
			.sort((a, b) => (a.title ?? "").localeCompare(b.title ?? "", undefined, { sensitivity: "base" }));
	}, [availableTypes, configFilters]);
	const contentTypeLabelMap = useMemo(() => {
		const map = new Map<string, string>();
		mergedTypes.forEach((entry) => {
			if (!entry.key) return;
			map.set(entry.key.toLowerCase(), entry.title ?? entry.key);
		});
		return map;
	}, [mergedTypes]);
	const resolveContentTypeLabel = useCallback(
		(contentType?: string | null) => {
			if (!contentType) return "—";
			const key = contentType.trim().toLowerCase();
			return contentTypeLabelMap.get(key) ?? contentType;
		},
		[contentTypeLabelMap]
	);
	const contentTypeFilters = useMemo(
		() =>
			[{ key: "all", title: "All" } as APAADMINBaseModelsContentTypeFilterItem]
				.concat(mergedTypes)
				.filter((entry) => !!entry.key),
		[mergedTypes]
	);
	const baseColumnCount = showPathColumn ? 7 : 6;
	const totalColumnCount = baseColumnCount + listFields.length;

	const isUnfilteredOverview =
		!isTypeLocked &&
		contentTypeFilter === "all" &&
		search.trim() === "" &&
		taxonomyFilterIds.length === 0 &&
		categoryFilterPathIds.length === 0;

	const activeTypeLabel = isTypeLocked
		? lockedConfig?.title ?? resolveContentTypeLabel(effectiveContentType)
		: resolveContentTypeLabel(contentTypeFilter);

	const overviewCards = useMemo(() => {
		if (!isUnfilteredOverview) return [];
		const configByKey = new Map<string, ContentTypeConfig>();
		(contentTypeConfigs ?? []).forEach((cfg) => {
			if (cfg?.id) configByKey.set(cfg.id.toLowerCase(), cfg);
			if (cfg?.title) configByKey.set(normalizeContentTypeKey(cfg.title), cfg);
		});
		return mergedTypes
			.filter((entry) => entry.key)
			.map((entry) => {
				const key = entry.key!;
				const cfg = configByKey.get(key.toLowerCase());
				return {
					key,
					title: cfg?.title ?? entry.title ?? key,
					description: cfg?.description ?? "",
					isMain: cfg?.isMain ?? entry.isMain ?? false
				};
			})
			// Pin the everyday editorial types (article → event → page) first, then the other "main"
			// massive types, then the rest — so the most-used content sits at the top of the overview.
			.sort(compareBrowseOrder);
	}, [contentTypeConfigs, isUnfilteredOverview, mergedTypes]);

	// Split the overview cards into the labelled visual groups (Actualités, Taux & marchés, …).
	// CONTENT_CATEGORIES is ordered by update cadence, so the domains an editor touches daily sit
	// at the top; `overviewCards` is already pin-sorted, so each group keeps article→event→page first.
	const overviewGroups = useMemo(() => {
		const byCategory = new Map<string, typeof overviewCards>();
		overviewCards.forEach((card) => {
			const categoryId = categorizeContentType(card.title, card.key);
			const bucket = byCategory.get(categoryId);
			if (bucket) bucket.push(card);
			else byCategory.set(categoryId, [card]);
		});
		return CONTENT_CATEGORIES.map((category) => ({
			...category,
			cards: byCategory.get(category.id) ?? []
		})).filter((group) => group.cards.length > 0);
	}, [overviewCards]);

	/** Reusable search box (Enter or button triggers the query). */
	const searchBox = (
		<div className="flex flex-1 items-end gap-2 min-w-[220px]">
			<div className="relative flex-1">
				<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
				<input
					className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-slate-400 focus:outline-none"
					placeholder={searchPlaceholder}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && onFilterOrSearchChange()}
				/>
			</div>
			<button
				className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
				onClick={() => onFilterOrSearchChange()}
			>
				Search
			</button>
		</div>
	);

	return (
		<div className="flex h-full relative">
			{loadingList && (
				<div className="absolute inset-0 z-40 overflow-hidden bg-slate-50 p-4 sm:p-6">
					{isUnfilteredOverview ? <SkeletonCards count={12} /> : <SkeletonList rows={9} />}
				</div>
			)}
			<div className="flex w-full gap-6 p-4 pb-16 sm:p-6 sm:pb-20">
				<div className="flex-1 min-w-0 space-y-4">
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div>
							<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Content</p>
							<h1 className="text-xl font-semibold text-slate-900">Browse and manage</h1>
							<p className="text-sm text-slate-600">
								{isUnfilteredOverview
									? "Pick a content type to browse its entries, or search across everything."
									: `Browsing ${activeTypeLabel}.`}
							</p>
						</div>
						<div className="flex items-center gap-2">
							<button
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
								onClick={handleOpenCreate}
								disabled={isTypeLocked && !lockedConfig}
							>
								<FiPlus /> Create
							</button>
							<button
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
								onClick={() => window.open(`${DEFAULT_DOMAIN}/`, "_blank")}
							>
								<FiExternalLink /> Open site
							</button>
							<ConnectionsModalButton className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" />
						</div>
					</div>

					{error && (
						<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
							{error}
						</div>
					)}

					{isUnfilteredOverview ? (
						/* ---------- OVERVIEW: search + content-type cards (no table, no chip wall) ---------- */
						<>
							<div className="flex flex-wrap items-center gap-2">
								{searchBox}
								<button
									className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
									onClick={() => loadList(page, pageSize)}
									title="Refresh"
								>
									<FiRefreshCw />
								</button>
							</div>
							<div className="space-y-7">
								{overviewGroups.map((group) => (
									<section key={`group-${group.id}`} className="space-y-3">
										<div className="flex items-baseline justify-between gap-3 border-b border-slate-100 pb-2">
											<div>
												<h2 className="text-sm font-semibold uppercase tracking-wider text-slate-800">
													{group.label}
												</h2>
												<p className="text-xs text-slate-500">{group.hint}</p>
											</div>
											<span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
												{group.cards.length}
											</span>
										</div>
										<div
											className="grid gap-3"
											style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}
										>
											{group.cards.map((card) => (
												<button
													key={`overview-${card.key}`}
													type="button"
													onClick={() => updateTypeParam(card.key)}
													className="group flex h-full flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-300"
												>
													<div className="flex items-start justify-between gap-2">
														<h3 className="text-sm font-semibold leading-tight text-slate-900 line-clamp-2">
															{card.title}
														</h3>
														{card.isMain && (
															<span className="shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
																Main
															</span>
														)}
													</div>
													<p className="text-xs leading-relaxed text-slate-500 line-clamp-2">
														{card.description?.trim() || `Browse ${card.title.toLowerCase()} entries.`}
													</p>
													<span className="mt-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 group-hover:text-slate-900">
														Open <FiChevronRight className="h-3 w-3" />
													</span>
												</button>
											))}
										</div>
									</section>
								))}
								{overviewGroups.length === 0 && (
									<div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-600">
										No content types available.
									</div>
								)}
							</div>
						</>
					) : (
						/* ---------- RESULTS: compact filter bar, results take the page ---------- */
						<>
							<div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
								<div className="flex flex-wrap items-center gap-2">
									{!isTypeLocked && (
										<button
											className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
											onClick={() => updateTypeParam("all")}
											title="Back to all content types"
										>
											<FiChevronLeft /> All
										</button>
									)}
									{!isTypeLocked ? (
										<select
											value={contentTypeFilter}
											onChange={(e) => updateTypeParam(e.target.value ?? "all")}
											className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800 focus:border-slate-400 focus:outline-none"
										>
											{contentTypeFilters.map((filter) => (
												<option key={`type-opt-${filter.key}`} value={filter.key ?? "all"}>
													{filter.title}
													{filter.isMain ? " ★" : ""}
												</option>
											))}
										</select>
									) : (
										<span className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white">
											{activeTypeLabel}
										</span>
									)}
									{searchBox}
									<button
										className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
										onClick={() => loadList(page, pageSize)}
										title="Refresh"
									>
										<FiRefreshCw />
									</button>
									<select
											value={`${sort.field}|${sort.desc ? "desc" : "asc"}`}
											onChange={(e) => {
												const [field, dir] = e.target.value.split("|");
												setSort({ field, desc: dir === "desc" });
												setPage(1);
											}}
											className="ml-auto rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 focus:border-slate-400 focus:outline-none"
											title="Trier"
										>
											{sort.field.startsWith("field:") && (() => {
												const key = sort.field.slice("field:".length);
												const isDate = key.split("-").includes("date") || key.includes("publication");
												const label = isDate
													? sort.desc
														? "Par date — plus récent"
														: "Par date — plus ancien"
													: `Colonne : ${key} ${sort.desc ? "↓" : "↑"}`;
												return <option value={`${sort.field}|${sort.desc ? "desc" : "asc"}`}>{label}</option>;
											})()}
											<option value="updated|desc">Plus récent</option>
											<option value="updated|asc">Plus ancien</option>
											<option value="title|asc">Titre A→Z</option>
											<option value="title|desc">Titre Z→A</option>
											<option value="type|asc">Type A→Z</option>
										</select>
										<div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
										<button
											type="button"
											onClick={() => setViewMode("list")}
											className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
												viewMode === "list" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"
											}`}
										>
											List
										</button>
										<button
											type="button"
											onClick={() => setViewMode("grid")}
											className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
												viewMode === "grid" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"
											}`}
										>
											Grid
										</button>
									</div>
								</div>
								{(supportsCategoryFilter || supportsTaxonomyFilter) && (
									<div className="flex flex-wrap items-end gap-3 border-t border-slate-100 pt-3">
										{supportsCategoryFilter && (
											<div className="min-w-[240px] max-w-[340px] flex-1">
												<CategoryPathSelect
													label="Categories"
													categories={categories}
													value={categoryFilterPathIds}
													onChange={onCategoryFilterChange}
													placeholder="Filter categories"
												/>
											</div>
										)}
										{supportsTaxonomyFilter && (
											<div className="min-w-[240px] max-w-[340px] flex-1">
												<TaxonomyMultiSelect
													label="Taxonomies"
													options={taxonomies}
													value={taxonomyFilterIds}
													onChange={onTaxonomyFilterChange}
													placeholder="Filter taxonomies"
												/>
											</div>
										)}
									</div>
								)}
							</div>

							{viewMode === "list" ? (
								<ContentListView
									items={items}
									listFields={listFields}
									showPathColumn={showPathColumn}
									slugColumnLabel={slugColumnLabel}
									totalColumnCount={totalColumnCount}
									hasTitleColumn={hasTitleColumn}
									sort={sort}
									onSort={handleSort}
									resolveContentTypeLabel={resolveContentTypeLabel}
									onNavigate={goToPage}
								/>
							) : (
								<ContentGridView
									items={items}
									resolveContentTypeLabel={resolveContentTypeLabel}
									onNavigate={goToPage}
								/>
							)}

							{totalCount > 0 && (
								<div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-2.5 shadow-sm backdrop-blur">
									<p className="text-sm text-slate-600">
										<span className="font-semibold text-slate-900">
											{(page - 1) * pageSize + 1}&ndash;{Math.min(page * pageSize, totalCount)}
										</span>{" "}
										of {totalCount.toLocaleString()}
									</p>
									<div className="flex items-center gap-1">
										<button
											type="button"
											disabled={!canPrev}
											onClick={() => goToPageNum(1)}
											className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
											title="First page"
										>
											<FiChevronsLeft />
										</button>
										<button
											type="button"
											disabled={!canPrev}
											onClick={() => goToPageNum(page - 1)}
											className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
											title="Previous page"
										>
											<FiChevronLeft />
										</button>
										<span className="px-2 text-sm text-slate-700">
											Page <span className="font-semibold text-slate-900">{page}</span> / {totalPages}
										</span>
										<button
											type="button"
											disabled={!canNext}
											onClick={() => goToPageNum(page + 1)}
											className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
											title="Next page"
										>
											<FiChevronRight />
										</button>
										<button
											type="button"
											disabled={!canNext}
											onClick={() => goToPageNum(totalPages)}
											className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
											title="Last page"
										>
											<FiChevronsRight />
										</button>
										<select
											value={pageSize}
											onChange={(event) => {
												const nextSize = Number(event.target.value);
												setPageSize(nextSize);
												setPage(1);
												loadList(1, nextSize);
											}}
											className="ml-2 rounded-lg border border-slate-200 px-2 py-2 text-xs font-semibold text-slate-700 focus:border-slate-400 focus:outline-none"
											title="Items per page"
										>
											{PAGE_SIZE_OPTIONS.map((size) => (
												<option key={size} value={size}>
													{size} / page
												</option>
											))}
										</select>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>
			<ContentTypePickerModal
				show={showCreateModal}
				onHide={() => setShowCreateModal(false)}
				createableTypes={createableTypes}
				onSelectType={handleSelectCreateType}
			/>
			<CreateContentFormModal
				show={showCreateForm}
				onHide={() => {
					setShowCreateForm(false);
					resetCreateState();
				}}
				createType={createType}
				createValues={createValues}
				setCreateValues={setCreateValues}
				createErrors={createErrors}
				createFormError={createFormError}
				creating={creating}
				categories={categories}
				taxonomies={taxonomies}
				onSubmit={handleCreate}
			/>
		</div>
	);
};

export default Content;
